import { Avatar } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import React, { ReactNode, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import styled from 'styled-components';

import ButtonLink from '@/components/input/ButtonLink';
import SearchInput from '@/components/input/SearchInput';
import Space from '@/components/layout/Space';
import SubTitle from '@/components/typography/SubTitle';
import { useFirstRenderEffect } from '@/hooks';

const BankLink = styled(ButtonLink)`
  ${({ theme }: { theme: Theme }): string => `
    align-items: center;
    color: initial;
    text-align: initial;
    text-decoration: none !important;
    outline: none !important;

    > span {    
      padding-bottom: ${theme.spacing()}px;
      padding-top: ${theme.spacing()}px;
    }
  
    &:hover > span {
      background-color: ${theme.colors.light};
    }

    &:focus > span {
      background-color: ${theme.colors.light};
    }`}
`;

export interface Bank {
  id: string;
  name: string;
  icon?: string;
}

interface BanksSelectionProps {
  'data-test'?: string;
  value: Bank[];
  searchClassName?: string;
  banksClassName?: string;
  bankItemClassName?: string;
  onSelect: (bank: string) => void | Promise<void>;
  LabelComponent?: React.FC<{ defaultComponent: ReactNode; value: Bank }>;
}

const BanksSelection: React.FC<BanksSelectionProps> = ({
  searchClassName,
  banksClassName,
  bankItemClassName,
  value,
  onSelect,
  LabelComponent,
  'data-test': dataTest,
}) => {
  const inputRef = React.useRef<HTMLElement>(null);
  useFirstRenderEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return (): void => {
      clearTimeout(timeout);
    };
  });
  const [searchText, setSearchText] = useState<string>('');
  const filteredValue = value.filter(
    ({ name: label }) => !searchText || label.toLowerCase().includes(searchText.toLowerCase()),
  );
  const cache = new CellMeasurerCache({
    defaultHeight: 40,
    fixedWidth: true,
  });
  return (
    <Space
      direction="vertical"
      size={2}
      style={{
        overflow: 'inherit',
        flexGrow: 1,
      }}
    >
      <SearchInput
        onChange={setSearchText}
        className={searchClassName}
        inputRef={inputRef}
        data-test={dataTest && `${dataTest}-search`}
      />
      <div
        style={{
          display: 'flex',
          flexGrow: 1,
        }}
        className={banksClassName}
      >
        {filteredValue.length > 0 && (
          <AutoSizer>
            {({ height, width }): ReactNode => (
              <List
                className="ym-hide-content"
                tabIndex={-1}
                overscanRowCount={10}
                style={{ outline: 'none' }}
                deferredMeasurementCache={cache}
                height={height}
                width={width}
                rowCount={filteredValue.length}
                rowHeight={cache.rowHeight}
                rowRenderer={({ key, index, style, parent }): ReactNode => {
                  const bank = filteredValue[index];
                  return (
                    <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                      {({ measure }): ReactNode => (
                        // FIXME: howto pass ref?
                        <BankLink
                          onClick={(): void | Promise<void> => onSelect(bank.id)}
                          style={style}
                          data-test={dataTest && `${dataTest}-bank-${bank.id}`}
                        >
                          <Space key={bank.id} size={1.5} className={bankItemClassName}>
                            {bank.icon ? (
                              <Avatar onLoad={measure} src={bank.icon} alt={bank.name} />
                            ) : (
                              <Avatar>{bank.name.substr(0, 1)}</Avatar>
                            )}
                            {LabelComponent ? (
                              <LabelComponent
                                defaultComponent={<SubTitle style={{ fontWeight: 400 }}>{bank.name}</SubTitle>}
                                value={bank}
                              />
                            ) : (
                              <Space direction="vertical" size={0} style={{ justifyContent: 'center' }}>
                                <SubTitle style={{ fontWeight: 400 }}>{bank.name}</SubTitle>
                              </Space>
                            )}
                          </Space>
                        </BankLink>
                      )}
                    </CellMeasurer>
                  );
                }}
              />
            )}
          </AutoSizer>
        )}
      </div>
    </Space>
  );
};

export default BanksSelection;
